import { useState } from 'react';
import { NJText } from '@engie-group/fluid-design-system-react';
import { UMButton, UMFormGroup, UMIcon, UMInput } from '../../urbanmap/_components';
import { PlanSegmentedControl } from './PlanSegmentedControl';
import { ProgressLabel, ProgressMark, type ProgressItem, type SectionKey, type SectionProgress } from './CreationProgress';
import { CommuneSearch } from './CommuneSearch';
import { ContractFields } from './ContractFields';
import { STATION_RADIUS_KM, centerOf, circlePolygon } from './domain';
import { NetworkPicker } from './NetworkPicker';
import { PerimeterMap } from './PerimeterMap';
import { PlaceSearch } from './PlaceSearch';
import { PlanFormStyles } from './PlanFormStyles';
import { StationCard } from './StationCard';
import { StationSection } from './StationSection';
import { PC, caption, card } from './tokens';
import type { ContractPeriod, DrawShape, LngLat, PlanBuilding, ProjectKind, Referential } from './types';

export type PerimeterMode = 'communes' | 'drawn';

export interface PerimeterDraft {
  name: string;
  kind: ProjectKind;
  networkIds: string[];
  contract: ContractPeriod;
  mode: PerimeterMode;
  communes: string[];
  shape: DrawShape;
  points: LngLat[];
  closed: boolean;
  drawing: boolean;
}

interface Props {
  referential: Referential;
  draft: PerimeterDraft;
  onChange: (patch: Partial<PerimeterDraft>) => void;
  included: PlanBuilding[];
  includedIds: Set<string>;
  /** Station météo retenue (section 3) et sélection. */
  stationId: string;
  onStationSelect: (id: string) => void;
  progress: SectionProgress;
  onTouch: (key: SectionKey) => void;
  actions: React.ReactNode;
  /** Configuration d'un projet existant : périmètre affiché en lecture seule avec ce message. */
  lockedPerimeter?: string;
}

const TABS: Record<PerimeterMode, string> = { communes: 'Communes', drawn: 'Zone sur la carte' };

function Section({ title, icon, item, onTouch, children }: { title: string; icon: string; item: ProgressItem; onTouch: () => void; children: React.ReactNode }) {
  const [focused, setFocused] = useState(false);
  const display = focused && item.state === 'untouched' ? { ...item, state: 'current' as const } : item;
  return (
    <li
      className="pcv3-creation-section"
      aria-label={title}
      aria-current={focused ? 'step' : undefined}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) { setFocused(false); onTouch(); }
      }}
    >
      <div className="pcv3-creation-section-heading" data-state={display.state}>
        <ProgressMark item={display} icon={icon} />
        <div className="pcv3-progress-copy" aria-live="polite">
          <NJText as="strong" scale="sm">{title}</NJText>
          <ProgressLabel item={display} />
        </div>
      </div>
      {children}
    </li>
  );
}

/** Étape 1 plein écran : identité du projet, réseaux repris, contrat et périmètre géographique. */
export function StepPerimeter({ referential, draft, onChange, included, includedIds, stationId, onStationSelect, progress, onTouch, actions, lockedPerimeter }: Props) {
  const [focus, setFocus] = useState<{ id: string; shape: LngLat[] } | null>(null);
  const [opened, setOpened] = useState<string | null>(null);
  const goTo = (insee: string) => {
    const shape = referential.communeOutlines[insee];
    if (shape) setFocus({ id: `${insee}-${Date.now()}`, shape });
  };
  const outlines: LngLat[][] = draft.mode === 'communes'
    ? draft.communes.map((c) => referential.communeOutlines[c]).filter((r): r is LngLat[] => !!r)
    : [];
  const perimeterReady = draft.mode === 'communes' ? draft.communes.length > 0 : draft.closed;
  const empty = perimeterReady && included.length === 0;
  const networks = referential.networks.filter((n) => draft.networkIds.includes(n.id));
  /** Les stations météo n'apparaissent qu'une fois le périmètre renseigné. */
  const stations = perimeterReady
    ? referential.stations.map((s) => ({ id: s.id, name: s.name, position: s.position, area: circlePolygon(s.position, STATION_RADIUS_KM) }))
    : [];
  const center = centerOf(included.map((b) => b.centroid));
  const openedStation = perimeterReady ? referential.stations.find((s) => s.id === opened) ?? null : null;

  return (
    <div className="pcv3-perimeter-layout" style={{ display: 'flex', height: '100%', minHeight: 0 }}>
      <PlanFormStyles />
      <aside className="pcv3-creation-sidebar" aria-label="Formulaire du projet">
      <ol className="plan-panel pcv3-creation-panel" aria-label="Rubriques du formulaire">
        <Section title="Nom du projet" icon="drive_file_rename_outline" item={progress.name} onTouch={() => onTouch('name')}>
          <UMFormGroup label="Nom du projet">
            <UMInput value={draft.name} onChange={(name) => onChange({ name })} placeholder="Ex. Gentilly — extension réseau 2028" ariaLabel="Nom du projet" />
            {!draft.name.trim() && <div style={{ ...caption, marginTop: 4 }}>Obligatoire pour passer à l’étape suivante.</div>}
          </UMFormGroup>
        </Section>

        <Section title="Périmètre géographique" icon="map" item={progress.perimeter} onTouch={() => onTouch('perimeter')}>
          <fieldset disabled={!!lockedPerimeter} style={{ border: 0, margin: 0, padding: 0, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {lockedPerimeter && <div style={caption}>{lockedPerimeter}</div>}
          <PlanSegmentedControl
            label="Mode de sélection du périmètre"
            options={Object.values(TABS)}
            value={TABS[draft.mode]}
            onChange={(v) => onChange({ mode: v === TABS.drawn ? 'drawn' : 'communes', drawing: false, shape: 'free' })}
          />

          {draft.mode === 'communes' ? (
            <CommuneSearch
              communes={referential.communes}
              selected={draft.communes}
              onChange={(communes) => onChange({ communes })}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <PlaceSearch communes={referential.communes} onPick={goTo} />
              <div style={caption}>
                {draft.drawing
                  ? 'Cliquez-glissez sur la carte pour tracer le contour, puis validez-le.'
                  : draft.points.length > 0 && !draft.closed
                    ? `Tracé en attente (${draft.points.length} sommets) : validez-le pour l’appliquer au projet.`
                    : draft.closed
                      ? `Contour validé (${draft.points.length} sommets) : seuls les bâtiments à l’intérieur sont copiés.`
                      : 'Recherchez le lieu à étudier, puis tracez son contour sur la carte.'}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <UMButton
                  icon="polyline"
                  variant={draft.drawing ? 'secondary' : 'primary'}
                  onClick={() => onChange(draft.drawing ? { drawing: false } : { drawing: true, points: [], closed: false })}
                >
                  {draft.drawing ? 'Annuler le tracé' : draft.points.length > 0 ? 'Redessiner' : 'Dessiner le contour'}
                </UMButton>
                <UMButton
                  icon="check"
                  disabled={draft.points.length < 3 || draft.closed}
                  onClick={() => onChange({ closed: true, drawing: false })}
                >
                  Valider le contour
                </UMButton>
                <UMButton variant="secondary" icon="delete" disabled={draft.points.length === 0} onClick={() => onChange({ points: [], closed: false, drawing: false })}>Effacer</UMButton>
              </div>
            </div>
          )}
          </fieldset>
        </Section>

        {empty && (
          <li
            role="status"
            style={{
              ...card, display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
              background: PC.dangerBg, borderColor: PC.dangerBorder, fontSize: 13,
            }}
          >
            <UMIcon name="error" size={20} color={PC.dangerText} />
            <span style={{ color: PC.dangerText }}>Aucun bâtiment UrbanMap dans ce périmètre : modifiez-le.</span>
          </li>
        )}

        <Section title="Station météo" icon="thermostat" item={progress.weather} onTouch={() => onTouch('weather')}>
          <StationSection
            stations={referential.stations}
            selected={stationId}
            center={center}
            ready={perimeterReady}
            onSelect={(id) => { onStationSelect(id); setOpened(id); }}
          />
        </Section>

        <Section title="Type de projet" icon="hub" item={progress.kind} onTouch={() => onTouch('kind')}>
          <NetworkPicker
            kind={draft.kind}
            onKindChange={(kind) => onChange({ kind, networkIds: kind === 'new' ? [] : draft.networkIds })}
            networks={referential.networks}
            selected={draft.networkIds}
            onSelectedChange={(networkIds) => onChange({ networkIds })}
            communes={draft.communes}
          />
        </Section>

        <Section title="Période du contrat" icon="date_range" item={progress.contract} onTouch={() => onTouch('contract')}>
          <ContractFields value={draft.contract} onChange={(contract) => onChange({ contract })} />
        </Section>
      </ol>
      {actions}
      </aside>

      <div className="pcv3-perimeter-map" style={{ position: 'relative', flex: 1, minWidth: 0 }}>
        <PerimeterMap
          buildings={referential.buildings}
          included={includedIds}
          outlines={outlines}
          networks={networks}
          focus={focus}
          drawing={draft.mode === 'drawn' && draft.drawing}
          shape={draft.shape}
          points={draft.mode === 'drawn' ? draft.points : []}
          closed={draft.closed}
          onPolygon={(points) => onChange({ points, drawing: false, closed: false })}
          stations={stations}
          selectedStation={stationId}
          onStationPick={setOpened}
          anchor={openedStation ? openedStation.position : null}
          search={{ communes: referential.communes, communeOutlines: referential.communeOutlines }}
        >
          {(pos) => openedStation && (
            <StationCard
              station={openedStation}
              selected={openedStation.id === stationId}
              at={pos}
              onChoose={() => onStationSelect(openedStation.id)}
              onClose={() => setOpened(null)}
            />
          )}
        </PerimeterMap>
        <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 5, ...card, padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
          <Legend color={PC.included} label="Inclus dans le projet" />
          <Legend color={PC.excluded} label="Bâtiment UrbanMap hors périmètre" />
          <Legend color={PC.outline} label="Contour des communes" dashed />
          {networks.length > 0 && <Legend color={PC.network} label="Réseau existant repris" line />}
          {stations.length > 0 && <Legend color={PC.accent} label="Station météo et sa zone" dashed />}
        </div>
      </div>
    </div>
  );
}

function Legend({ color, label, dashed, line }: { color: string; label: string; dashed?: boolean; line?: boolean }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span
        style={dashed || line
          ? { width: 14, height: 0, borderTop: `${line ? 3 : 2}px ${dashed ? 'dashed' : 'solid'} ${color}`, display: 'inline-block' }
          : { width: 14, height: 10, background: color, opacity: 0.8 }}
      />
      {label}
    </span>
  );
}
